/**
 * Shared visual grammar for every landing scene.
 *
 * Every bespoke scene (mesh, feed, metrics, chart, globe, radar) imports
 * these tokens so the whole landing reads as one instrument — same stroke
 * weights, same dot sizes, same monospace label style, same color palette.
 * If you see a rgba(242, 238, 228, ...) string anywhere outside this file,
 * it's a bug to fix.
 */

export const CREAM_RGB = "242, 238, 228";
export const BRICK_RGB = "196, 69, 54";
export const CYAN_RGB = "80, 210, 255";
export const AMBER_RGB = "255, 176, 70";
export const MAGENTA_RGB = "255, 80, 180";

export const CREAM: [number, number, number] = [242, 238, 228];
export const BRICK: [number, number, number] = [196, 69, 54];
export const CYAN: [number, number, number] = [80, 210, 255];
export const AMBER: [number, number, number] = [255, 176, 70];
export const MAGENTA: [number, number, number] = [255, 80, 180];

export const STROKE = {
  hairline: 0.5,
  default: 1,
  data: 1.5,
  highlight: 2,
} as const;

export const DOT = {
  micro: 1,
  small: 1.6,
  default: 2.2,
  active: 3,
  pulse: 4,
} as const;

/** Alpha values used for each tier of visual importance. */
export const ALPHA = {
  gridFaint: 0.006,
  gridMajor: 0.014,
  guideDim: 0.1,
  guideLine: 0.18,
  lineDim: 0.25,
  lineDefault: 0.55,
  lineActive: 0.85,
  labelMicro: 0.28,
  labelDim: 0.42,
  labelDefault: 0.6,
  labelActive: 0.85,
} as const;

export const EASE = {
  out: "cubic-bezier(0.16, 1, 0.3, 1)",
  inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

/** Monospace label styles used by every scene's tiny annotations. */
export const LABEL = {
  micro: {
    fontFamily: `"IBM Plex Mono", ui-monospace, monospace`,
    fontSize: 9,
    letterSpacing: 3,
    textTransform: "uppercase" as const,
  },
  small: {
    fontFamily: `"IBM Plex Mono", ui-monospace, monospace`,
    fontSize: 10,
    letterSpacing: 3,
    textTransform: "uppercase" as const,
  },
  standard: {
    fontFamily: `"IBM Plex Mono", ui-monospace, monospace`,
    fontSize: 11,
    letterSpacing: 4,
    textTransform: "uppercase" as const,
  },
  emphasis: {
    fontFamily: `"IBM Plex Mono", ui-monospace, monospace`,
    fontSize: 14,
    letterSpacing: 5,
    textTransform: "uppercase" as const,
  },
} as const;

/** Utility: rgba() string from cream/brick tuples. */
export function cream(alpha: number): string {
  return `rgba(${CREAM_RGB}, ${alpha})`;
}

export function brick(alpha: number): string {
  return `rgba(${BRICK_RGB}, ${alpha})`;
}

export function cyan(alpha: number): string {
  return `rgba(${CYAN_RGB}, ${alpha})`;
}

export function amber(alpha: number): string {
  return `rgba(${AMBER_RGB}, ${alpha})`;
}
